/* Teplota zakázky — jak dlouho už nemovitost stojí v jedné fázi.
 *
 * PROČ TENHLE SOUBOR EXISTUJE:
 * Makléřova bolest č. 3 zní „nemovitost se půl roku neprodá a chybí komunikace
 * s klientem, aby to vypadalo, že se něco děje". Teplota je jediné místo, kde
 * to appka řekne dřív než majitel. Když zakázka stojí ve fázi 60 dní, řádek
 * v pipeline zežloutne; po 120 dnech zčervená.
 *
 * MĚŘÍ SE STÁNÍ VE FÁZI, NE STÁŘÍ ZÁZNAMU. Server posílá `stav_od` = kdy
 * zakázka do téhle fáze vstoupila (routes/zakazky.py → `_stav_od`). Kdyby se
 * počítalo z `updated_at`, stačilo by opravit překlep v adrese a zakázka
 * stojící 200 dní by vypadala jako čerstvá.
 *
 * NEZNÁMÉ NENÍ NULA. Zakázka bez obchodu (sirotek) nemá odkud vstup do fáze
 * vzít — vrací se `null` a obrazovka napíše pomlčku. „0 dní" by tvrdilo, že
 * se právě teď něco stalo.
 */

/** Od kolika dnů ve fázi je zakázka vlažná (žlutá). */
export const PRAH_VLAZNO = 60;
/** Od kolika dnů ve fázi je horká (červená). */
export const PRAH_HORKO = 120;

const DEN = 86400000;

/**
 * Kolik dní zakázka stojí v téhle fázi. `null` = nevíme.
 * @param {string|null|undefined} stavOd ISO čas vstupu do fáze ze serveru
 * @param {number} ted čas „teď" v ms (parametr kvůli testům)
 */
export function dniVeFazi(stavOd, ted = Date.now()) {
  if (!stavOd || typeof stavOd !== "string") return null;
  const t = Date.parse(stavOd);
  if (!Number.isFinite(t)) return null;
  const rozdil = ted - t;
  /* Čas v budoucnu (rozjeté hodiny na serveru nebo ručně zadané datum) je
     nula dní, ne záporné číslo. Záporná teplota nedává smysl a v součtu
     průměru by táhla ostatní zakázky dolů. */
  if (rozdil <= 0) return 0;
  return Math.floor(rozdil / DEN);
}

/**
 * Stupeň teploty. Dokončená zakázka je vždy klidná — u předané nemovitosti
 * nemá smysl křičet, že fáze trvá dlouho.
 * @returns {"neznamo"|"klid"|"vlazno"|"horko"}
 */
export function teplotaFaze(dni, { dokonceno = false } = {}) {
  if (dokonceno) return "klid";
  if (dni == null) return "neznamo";
  if (dni >= PRAH_HORKO) return "horko";
  if (dni >= PRAH_VLAZNO) return "vlazno";
  return "klid";
}

/** Barva textu teploty. Jen tokeny motivu — hex by v druhém motivu svítil. */
export function barvaTeploty(stupen) {
  if (stupen === "horko") return "var(--negative, #BB5B43)";
  if (stupen === "vlazno") return "var(--warn, #C49A3E)";
  return "var(--muted)";
}

/**
 * Podbarvení řádku. Vlažná zakázka žloutne, horká červená — přesně tohle si
 * makléř přál vidět, aniž by cokoli otevíral. Průhlednost je nízká schválně:
 * text na řádku musí zůstat čitelný v obou motivech.
 */
export function pozadiTeploty(stupen) {
  if (stupen === "horko") return "color-mix(in srgb, var(--negative, #BB5B43) 12%, var(--surface))";
  if (stupen === "vlazno") return "color-mix(in srgb, var(--warn, #C49A3E) 12%, var(--surface))";
  return "var(--surface)";
}

/** Klíč do slovníku s popiskem teploty. `null` = nic se nepíše.
 *
 * `sada` říká, ČÍ je to řádek. U prodeje zní věta „ozvi se majiteli", u nákupu
 * ale žádný majitel není — je tam kupující, který se dva měsíce neozval. Kdyby
 * obě strany sdílely jednu větu, radila by makléři u kupujícího volat člověku,
 * který v tom obchodu vůbec nefiguruje.
 */
export function popisekTeploty(stupen, sada = "zakazky") {
  if (stupen === "horko") return `${sada}.teplotaHorko`;
  if (stupen === "vlazno") return `${sada}.teplotaVlazno`;
  return null;
}
